import Page from '@shell/Page'
import Header from '@ui/elements/Header'
import { StepOptions, StepVariable } from '@order/steps/types'
import { View } from 'react-native'
import { Recommendations, RecommendUtils } from 'recommend/RecommendUtils'
import OptionPackagesAttributes from './Option/OptionPackagesAttributes'
import { useRecommendedScroll } from './useRecommendedScroll'

import ButtonsFixed, { SpaceButtons } from '@ui/buttons/ButtonsFixed'
import PackageAttributeInfoModal from '@order/modals/PackageAttributeInfo'
import { getFirstActive } from '@order/modals/PackageAttributeInfo/useOptionAttributes'
import useModalDataState from '@order/hooks/useModalDataState'
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSpring,
} from 'react-native-reanimated'
import Button from '@ui/buttons/Button'
import { buttonsFixed, gap } from '@ui/theme/values'
import Icon from '@ui/elements/Icon'
import { useEffect } from 'react'
import { useConfigCondition } from 'features/config/useConfigCondition'

interface Props {
  title?: string
  desc?: string
  options: StepOptions
  recommendations?: Recommendations
  variable?: StepVariable | null
}

const ICON_ANIMATION_DISTANCE = 3

export default function PackagesAttributesTemplate(props: Props) {
  const { title, desc, options, recommendations } = props

  const modal = useModalDataState({
    isOpen: false,
    data: getFirstActive(options),
  })

  const isRecommendButtonEnabled = useConfigCondition(
    'recommendations:packages.button'
  )

  const { refs, handlers, animated } = useRecommendedScroll({
    offset: buttonsFixed.height,
    enabled: isRecommendButtonEnabled,
  })

  const hasRecommended = recommendations?.hasRecommendations

  const buttonsFixedStyle = useAnimatedStyle(() => {
    return {
      opacity: animated.visible.value,
      pointerEvents: animated.visible.value > 0.5 ? 'auto' : 'none',
      transform: [
        { translateY: interpolate(animated.visible.value, [0, 1], [200, 0]) },
      ],
    }
  })

  const iconAnimation = useSharedValue(0)

  useEffect(() => {
    if (!isRecommendButtonEnabled) {
      return
    }
    iconAnimation.set(
      withRepeat(
        withSpring(ICON_ANIMATION_DISTANCE * 2, {
          damping: 20,
          mass: 0.8,
        }),
        -1,
        true
      )
    )
  }, [iconAnimation, isRecommendButtonEnabled])

  const arrowDownStyle = useAnimatedStyle(() => {
    return {
      transform: [
        { translateY: iconAnimation.value - ICON_ANIMATION_DISTANCE },
      ],
    }
  })

  return (
    <>
      <Page
        scrollViewRef={refs.scroll}
        onScroll={handlers.onScroll}
        stage="configuration"
      >
        <Header title={title} desc={desc} />
        <View className="flex flex-col gap-8 px-8">
          {options.map((option) => {
            const isRecommended = RecommendUtils.options.isRecommended(
              recommendations,
              option
            )
            return (
              <OptionPackagesAttributes
                key={option.id}
                options={options.length}
                onAttributePress={(index: number) => {
                  modal.open({ optionId: option.id, activeIndex: index })
                }}
                isRecommended={isRecommended}
                tileRef={isRecommended ? refs.recommended : undefined}
                onLayout={
                  isRecommended ? handlers.onRecommendationLayout : undefined
                }
                {...option}
              />
            )
          })}
        </View>
        <SpaceButtons />
      </Page>
      {isRecommendButtonEnabled && hasRecommended && (
        <Animated.View style={buttonsFixedStyle}>
          <ButtonsFixed.Gradient />
          <View
            className="absolute left-0 right-0 flex flex-row items-center justify-center"
            style={{ bottom: 2 * gap }}
          >
            <Button
              size="md"
              variant="outlined"
              label="ui:recommendation"
              suffix={
                <Animated.View style={arrowDownStyle}>
                  <Icon name="arrow-down" />
                </Animated.View>
              }
              onPress={handlers.onPress}
            />
          </View>
        </Animated.View>
      )}
      <PackageAttributeInfoModal
        open={modal.isOpen}
        close={modal.close}
        active={modal.data}
        setActive={modal.open}
        options={options}
      />
    </>
  )
}
